import { useState, useEffect } from "react";
import { Reveal, Overline } from "./Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const empty = {
  full_name: "",
  email: "",
  phone: "",
  age: "",
  height: "",
  current_weight: "",
  goal: "",
  training_location: "",
  experience_level: "",
  injuries: "",
  biggest_struggle: "",
  about_you: "",
  ready_to_start: "",
  program_interest: "",
};

const fieldCls =
  "bg-[#0A0A0A] border-[#262626] focus-visible:border-[#0055FF] focus-visible:ring-0 text-white rounded-none h-12 placeholder:text-[#5a5a5a]";

export const ApplicationForm = () => {
  const [form, setForm] = useState(empty);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, program_interest: e.detail || "" }));
    window.addEventListener("prefill-program", handler);
    return () => window.removeEventListener("prefill-program", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const required = [
      "full_name", "email", "phone", "age", "height", "current_weight",
      "goal", "training_location", "experience_level", "biggest_struggle", "ready_to_start",
    ];
    const missing = required.filter((k) => !String(form[k]).trim());
    if (missing.length) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the policies to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, legal_agreed: agreed });
      setDone(true);
      window.scrollTo({ top: document.getElementById("apply").offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" className="relative py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal className="text-center">
          <Overline>Apply For Coaching</Overline>
          <h2 className="font-display font-semibold uppercase tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
            Start Your Transformation
          </h2>
          <p className="mt-5 text-[#A3A3A3] max-w-xl mx-auto">
            Every client starts here. Complete the application and I’ll personally review
            it, then reach out with the next steps.
          </p>
        </Reveal>

        {done ? (
          <Reveal>
            <div
              data-testid="application-success"
              className="mt-12 bg-[#141414] border border-[#0055FF] p-10 text-center shadow-[0_0_40px_rgba(0,85,255,0.15)]"
            >
              <CheckCircle2 size={48} className="text-[#0055FF] mx-auto" />
              <h3 className="font-display font-semibold text-2xl text-white mt-6">
                Application Received
              </h3>
              <p className="mt-4 text-[#A3A3A3] leading-relaxed">
                Thank you for applying to Howell Transformations. I’ll personally review
                your application and contact you within 24–48 hours with the next steps
                and my coaching recommendation.
              </p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form
              data-testid="application-form"
              onSubmit={submit}
              className="mt-12 bg-[#141414] border border-[#262626] p-6 sm:p-10 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Full Name *"><Input data-testid="app-full_name" className={fieldCls} value={form.full_name} onChange={set("full_name")} placeholder="John Smith" /></Field>
                <Field label="Email Address *"><Input data-testid="app-email" type="email" className={fieldCls} value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
                <Field label="Phone Number *"><Input data-testid="app-phone" className={fieldCls} value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" /></Field>
                <Field label="Age *"><Input data-testid="app-age" className={fieldCls} value={form.age} onChange={set("age")} placeholder="28" /></Field>
                <Field label="Height *"><Input data-testid="app-height" className={fieldCls} value={form.height} onChange={set("height")} placeholder={"5'10\""} /></Field>
                <Field label="Current Weight *"><Input data-testid="app-current_weight" className={fieldCls} value={form.current_weight} onChange={set("current_weight")} placeholder="210 lbs" /></Field>
              </div>

              <Field label="Your Goal *">
                <Input data-testid="app-goal" className={fieldCls} value={form.goal} onChange={set("goal")} placeholder="Lose 40 lbs and build muscle" />
              </Field>

              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Gym or Home *">
                  <Select value={form.training_location} onValueChange={set("training_location")}>
                    <SelectTrigger data-testid="app-training_location" className={fieldCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] text-white">
                      <SelectItem value="Gym">Gym</SelectItem>
                      <SelectItem value="Home">Home</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Experience Level *">
                  <Select value={form.experience_level} onValueChange={set("experience_level")}>
                    <SelectTrigger data-testid="app-experience_level" className={fieldCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] text-white">
                      <SelectItem value="Beginner">Beginner</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Injuries or Limitations">
                <Input data-testid="app-injuries" className={fieldCls} value={form.injuries} onChange={set("injuries")} placeholder="None / describe any injuries" />
              </Field>

              <Field label="Biggest Struggle *">
                <Textarea data-testid="app-biggest_struggle" className={`${fieldCls} h-24 py-3`} value={form.biggest_struggle} onChange={set("biggest_struggle")} placeholder="What's been holding you back?" />
              </Field>

              <Field label="Tell Me About Yourself">
                <Textarea data-testid="app-about_you" className={`${fieldCls} h-24 py-3`} value={form.about_you} onChange={set("about_you")} placeholder="Lifestyle, schedule, past experience..." />
              </Field>

              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="When Are You Ready To Start? *">
                  <Select value={form.ready_to_start} onValueChange={set("ready_to_start")}>
                    <SelectTrigger data-testid="app-ready_to_start" className={fieldCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] text-white">
                      <SelectItem value="Immediately">Immediately</SelectItem>
                      <SelectItem value="Within 1 week">Within 1 week</SelectItem>
                      <SelectItem value="Within 2 weeks">Within 2 weeks</SelectItem>
                      <SelectItem value="Within a month">Within a month</SelectItem>
                      <SelectItem value="Just exploring">Just exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Program Of Interest">
                  <Input data-testid="app-program_interest" className={fieldCls} value={form.program_interest} onChange={set("program_interest")} placeholder="Optional" />
                </Field>
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="app-legal-agree-label">
                <input
                  type="checkbox"
                  data-testid="app-legal-agree"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#0055FF] shrink-0"
                />
                <span className="text-xs text-[#A3A3A3] leading-relaxed">
                  I have read and agree to the{" "}
                  <a href="/legal/waiver" target="_blank" rel="noopener noreferrer" className="text-[#3377FF] underline">Coaching Waiver</a>,{" "}
                  <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-[#3377FF] underline">Terms & Conditions</a>,{" "}
                  <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#3377FF] underline">Privacy Policy</a>, and{" "}
                  <a href="/legal/cancellation" target="_blank" rel="noopener noreferrer" className="text-[#3377FF] underline">Cancellation Policy</a>.
                </span>
              </label>

              <button
                type="submit"
                data-testid="app-submit-button"
                disabled={submitting}
                className="w-full bg-[#0055FF] text-white hover:bg-[#3377FF] transition-colors font-bold px-8 py-4 uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? <><Loader2 size={18} className="animate-spin" /> Submitting...</> : "Submit My Application"}
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</Label>
    {children}
  </div>
);
